import {
  buildStudioHumanizationDimensionDisplays,
  type StudioHumanizationDimensionKey,
} from "./improvementHints";
import {
  STUDIO_SCENARIO_PRESETS,
  type StudioScenarioPreset,
} from "./scenarioPresets";
import type { StudioImproveLoopReadModel } from "./studioImproveReadModel";
import type { StudioPremiumDemoModel } from "./studioPremiumDemo";
import { buildStudioCanaryStateSummary } from "./studioCanaryState";
import {
  getStudioApprovedVoiceOption,
  getEnabledHumanizationLabels,
  getStudioPublishApprovalChecklistItems,
  getStudioWorkspaceVoicePersona,
  type StudioWorkspaceState,
} from "./studioWorkspaceState";
import type { StudioDeploymentDecision } from "./studioDeploymentState";

export const STUDIO_EXPORT_PACKAGE_VERSION = "a24.6_v1" as const;
export const STUDIO_PUBLISH_PACKAGE_VERSION = "a24.6_v1" as const;

export type StudioExportScenarioStatus = "ready" | "review" | "blocked";

export type StudioChecklistItem = {
  label: string;
  status: "done" | "pending" | "blocked";
  detail: string;
};

export type StudioCanaryPackageSummary = {
  state: string;
  stateLabel: string;
  headline: string;
  summary: string;
  tenantRestrictions: string[];
  promotionBlocked: boolean;
  promotionNote: string;
};

export type StudioExportPackage = {
  version: typeof STUDIO_EXPORT_PACKAGE_VERSION;
  mode: "audit_safe_demo_package";
  generatedAt: string;
  auditSafe: true;
  deploymentActivationTriggered: false;
  nonLiveBoundaryStatement: string;
  supervisedBookingMilestone: {
    runtimeReadyLabel: string;
    milestoneProofStatus: "pending_first_supervised_real_booking";
    milestoneProofLabel: string;
    milestoneProofDetail: string;
    firstBundleRequirements: string[];
  };
  assistantBrief: {
    assistantName: string;
    description: string;
    starterTone: string;
    starterPrompt: string;
    operatorDescription: string;
    recommendedCapabilities: string[];
    blockedCapabilities: string[];
    humanizationToggles: string[];
  };
  previewPersonaSelection: {
    personaId: string;
    personaLabel: string;
    personaSummary: string;
    approvedVoiceId: string;
    approvedVoiceLabel: string;
    approvedVoiceScope: string;
    selectedWorkspacePreviewMode: string;
    providerMode: string;
    modelLabel: string;
    allowedScenarioChannels: string[];
    routingNote: string;
  };
  scenarioResultsSummary: {
    totalScenarios: number;
    readyCount: number;
    reviewCount: number;
    blockedCount: number;
    scenarios: Array<{
      id: string;
      label: string;
      channel: string;
      status: StudioExportScenarioStatus;
      summary: string;
      evidence: string;
      latestEvidence: null | {
        recordedAt: string;
        requestId: string | null;
        personaLabel: string | null;
        sourceLabel: string;
      };
    }>;
  };
  humanizationScoreSummary: {
    requestId: string;
    status: "pass" | "warn" | "fail";
    totalScore: number;
    maxScore: number;
    dimensions: Array<{
      key: StudioHumanizationDimensionKey;
      label: string;
      score: number;
      maxScore: number;
      status: "pass" | "warn" | "fail";
    }>;
    recommendations: string[];
    comparison: {
      overallDelta: number;
      headline: string;
      improvedDimensions: string[];
    };
  };
  deploymentReadinessStatus: {
    state: StudioDeploymentDecision["state"];
    stateLabel: string;
    summary: string;
    blockers: string[];
    canaryRestrictions: string[];
  };
  rolloutFlags: {
    previewOnlyUntilA21: true;
    twilioProductionRoutingUntouched: true;
    nonLiveBoundaryStatement: string;
    liveDeploymentEnabled: false;
    webhookCutoverEnabled: false;
    activationPolicy: "manifest_only_no_live_activation";
  };
  canarySummary: StudioCanaryPackageSummary;
  nextStepChecklist: StudioChecklistItem[];
  operatorNotes: string | null;
};

export type StudioPublishPackage = {
  version: typeof STUDIO_PUBLISH_PACKAGE_VERSION;
  mode: "internal_only_publish_package";
  generatedAt: string;
  internalOnly: true;
  deploymentActivationTriggered: false;
  liveDeploymentEnabled: false;
  twilioProductionRoutingUntouched: true;
  nonLiveBoundaryStatement: string;
  supervisedBookingMilestone: {
    runtimeReadyLabel: string;
    milestoneProofStatus: "pending_first_supervised_real_booking";
    milestoneProofLabel: string;
    milestoneProofDetail: string;
    firstBundleRequirements: string[];
  };
  approval: {
    status:
      | "pending_acknowledgements"
      | "ready_for_internal_signoff"
      | "signed_off_internal_only";
    statusLabel: string;
    approvalTimestamp: string | null;
    checklistComplete: boolean;
    checklist: Array<{
      id: string;
      label: string;
      detail: string;
      acknowledged: boolean;
    }>;
  };
  approvedBriefSnapshot: {
    assistantName: string;
    description: string;
    starterTone: string;
    starterPrompt: string;
    operatorDescription: string;
    recommendedCapabilities: string[];
    blockedCapabilities: string[];
  };
  approvedPersonaAndControls: {
    personaId: string;
    personaLabel: string;
    personaSummary: string;
    approvedVoiceId: string;
    approvedVoiceLabel: string;
    approvedVoiceScope: string;
    selectedWorkspacePreviewMode: string;
    providerMode: string;
    modelLabel: string;
    enabledHumanizationToggles: string[];
    deploymentNotes: string | null;
    readinessAcknowledgements: string[];
    approvalAcknowledgements: string[];
  };
  previewEvidenceSummary: {
    totalEntries: number;
    scenarioRunCount: number;
    textPreviewCount: number;
    latestRecordedAt: string | null;
    latestRequestId: string | null;
    latestScenarioLabel: string | null;
    latestChannel: string | null;
    latestPersonaLabel: string | null;
  };
  humanizationSummary: {
    requestId: string;
    status: "pass" | "warn" | "fail";
    totalScore: number;
    maxScore: number;
    strongestDimension: string;
    weakestDimension: string;
    comparisonHeadline: string;
  };
  readinessDecision: {
    state: string;
    stateLabel: string;
    headline: string;
    summary: string;
    blockerCount: number;
    nextActions: string[];
  };
  blockerList: string[];
  canaryRestrictions: string[];
  canarySummary: StudioCanaryPackageSummary;
  nextStepChecklist: StudioChecklistItem[];
};

const SECRET_REPLACEMENTS: Array<[RegExp, string]> = [
  [/\b(Bearer)\s+[A-Za-z0-9._-]+\b/gi, "$1 [redacted]"],
  [/\b(ELEVENLABS_API_KEY\s*[:=]\s*)([^\s,;]+)/gi, "$1[redacted]"],
  [/\b(API[_ -]?KEY\s*[:=]\s*)([^\s,;]+)/gi, "$1[redacted]"],
  [/\b(TOKEN\s*[:=]\s*)([^\s,;]+)/gi, "$1[redacted]"],
  [/\b(SECRET\s*[:=]\s*)([^\s,;]+)/gi, "$1[redacted]"],
];

function sanitizeStudioExportText(value: string) {
  return SECRET_REPLACEMENTS.reduce((current, [pattern, replacement]) => {
    return current.replace(pattern, replacement);
  }, String(value || ""));
}

function sanitizeStudioExportValue<T>(value: T): T {
  if (typeof value === "string") {
    return sanitizeStudioExportText(value) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeStudioExportValue(item)) as T;
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        sanitizeStudioExportValue(item),
      ])
    ) as T;
  }

  return value;
}

function buildFallbackDeploymentDecision(
  premiumDemo: StudioPremiumDemoModel
): StudioDeploymentDecision {
  return {
    checkedAt: null,
    state: "deploy_blocked",
    stateLabel: "deploy-blocked",
    headline: "Deploy blocked",
    summary: premiumDemo.deployReadiness.summary,
    blockers: premiumDemo.deployReadiness.blockers.map((detail, index) => ({
      code: `premium_demo_blocker_${index + 1}`,
      category: "launch_constraint",
      label: detail,
      detail,
    })),
    nextActions: [premiumDemo.deployReadiness.suggestedNextStep],
    statusCards: [],
  };
}

function deploymentLaunchControlReady(decision: StudioDeploymentDecision) {
  return !decision.blockers.some(
    (blocker) =>
      blocker.category === "launch_constraint" ||
      blocker.code === "launch_control_missing" ||
      blocker.code === "launch_control_unavailable"
  );
}

function deploymentRolloutManifestPresent(decision: StudioDeploymentDecision) {
  return !decision.blockers.some(
    (blocker) =>
      blocker.code === "rollout_snapshot_missing" ||
      blocker.code === "rollout_readiness_unavailable"
  );
}

type StudioOperationalScenarioCopy = {
  readySummary: string;
  reviewSummary: string;
  blockedSummary: string;
  defaultSummary: string;
  readyEvidence: string;
  reviewEvidenceTail: string;
  blockedEvidenceTail: string;
  defaultEvidence: string;
};

const STUDIO_OPERATIONAL_SCENARIO_EXPORT_COPY: Partial<
  Record<StudioScenarioPreset["id"], StudioOperationalScenarioCopy>
> = {
  payment_not_ready: {
    readySummary:
      "Latest recorded payment-not-ready rehearsal is clear for internal operator review.",
    reviewSummary:
      "Latest recorded payment-not-ready rehearsal needs another tuning pass.",
    blockedSummary:
      "Latest recorded payment-not-ready rehearsal was blocked in the preview lane.",
    defaultSummary:
      "Payment-not-ready rehearsal is ready for internal preview review.",
    readyEvidence:
      "Payment wording stays operational, the follow-up path stays explicit, and no payment authority is implied.",
    reviewEvidenceTail:
      "Keep payment wording operational and staff-safe without pressuring payment.",
    blockedEvidenceTail:
      "Keep payment-not-ready handling inside preview rehearsal only.",
    defaultEvidence:
      "Scenario stays inside the approved preview lane and keeps payment-not-ready follow-through staff-safe and rehearsal-only.",
  },
  distance_timing_hesitation: {
    readySummary:
      "Latest recorded distance/timing hesitation rehearsal is clear for internal operator review.",
    reviewSummary:
      "Latest recorded distance/timing hesitation rehearsal needs another tuning pass.",
    blockedSummary:
      "Latest recorded distance/timing hesitation rehearsal was blocked in the preview lane.",
    defaultSummary:
      "Distance/timing hesitation rehearsal is ready for internal preview review.",
    readyEvidence:
      "The path stays practical and staff-safe without implying unsupported routing flexibility.",
    reviewEvidenceTail:
      "Keep the next step practical without implying unsupported flexibility or exceptions.",
    blockedEvidenceTail:
      "Keep distance/timing hesitation handling inside preview rehearsal only.",
    defaultEvidence:
      "Scenario stays inside the approved preview lane and keeps distance/timing follow-through practical, bounded, and rehearsal-only.",
  },
  renewal_clarification: {
    readySummary:
      "Latest recorded renewal clarification rehearsal is clear for internal operator review.",
    reviewSummary:
      "Latest recorded renewal clarification rehearsal needs another tuning pass.",
    blockedSummary:
      "Latest recorded renewal clarification rehearsal was blocked in the preview lane.",
    defaultSummary:
      "Renewal clarification rehearsal is ready for internal preview review.",
    readyEvidence:
      "The state-card timeline versus the office seven-month renewal path, and telephone renewal handling, stay staff-confirmed without implying broader renewal authority.",
    reviewEvidenceTail:
      "Keep the state-card timeline distinct from the office seven-month renewal path, and keep telephone handling staff-confirmed without guessing on the current public path.",
    blockedEvidenceTail:
      "Keep renewal clarification handling inside preview rehearsal only.",
    defaultEvidence:
      "Scenario stays inside the approved preview lane and keeps renewal clarification wording bounded, explicit, and rehearsal-only.",
  },
  transfer_intake_clarification: {
    readySummary:
      "Latest recorded transfer intake clarification rehearsal is clear for internal operator review.",
    reviewSummary:
      "Latest recorded transfer intake clarification rehearsal needs another tuning pass.",
    blockedSummary:
      "Latest recorded transfer intake clarification rehearsal was blocked in the preview lane.",
    defaultSummary:
      "Transfer intake clarification rehearsal is ready for internal preview review.",
    readyEvidence:
      "First-visit path status, current public telehealth status, and next-step ownership stay staff-confirmed without implying a completed transfer or broader telehealth authority.",
    reviewEvidenceTail:
      "Keep first-visit path status, current public telehealth status, and next-step ownership explicit without overclaiming the current public path.",
    blockedEvidenceTail:
      "Keep transfer intake clarification handling inside preview rehearsal only.",
    defaultEvidence:
      "Scenario stays inside the approved preview lane and keeps first-visit path status, current public telehealth status, and next-step ownership bounded, explicit, and rehearsal-only.",
  },
  veteran_funding_boundary: {
    readySummary:
      "Latest recorded veteran/funding boundary rehearsal is clear for internal operator review.",
    reviewSummary:
      "Latest recorded veteran/funding boundary rehearsal needs another tuning pass.",
    blockedSummary:
      "Latest recorded veteran/funding boundary rehearsal was blocked in the preview lane.",
    defaultSummary:
      "Veteran/funding boundary rehearsal is ready for internal preview review.",
    readyEvidence:
      "Veteran pricing and funding-style questions stay inside the approved lane and move to staff confirmation without guessing on exceptions.",
    reviewEvidenceTail:
      "Keep veteran pricing and funding-style wording inside the approved lane without implying non-public support paths.",
    blockedEvidenceTail:
      "Keep veteran/funding boundary handling inside preview rehearsal only.",
    defaultEvidence:
      "Scenario stays inside the approved preview lane and keeps veteran/funding boundary wording bounded, explicit, and rehearsal-only.",
  },
  distance_location_renewal_transfer: {
    readySummary:
      "Latest recorded distance/location renewal-transfer rehearsal is clear for internal operator review.",
    reviewSummary:
      "Latest recorded distance/location renewal-transfer rehearsal needs another tuning pass.",
    blockedSummary:
      "Latest recorded distance/location renewal-transfer rehearsal was blocked in the preview lane.",
    defaultSummary:
      "Distance/location renewal-transfer rehearsal is ready for internal preview review.",
    readyEvidence:
      "Distance or location hesitation around renewal or transfer planning stays practical and staff-safe without implying location exceptions that are not confirmed.",
    reviewEvidenceTail:
      "Keep the next step practical and location-sensitive without implying unsupported renewal or transfer exceptions.",
    blockedEvidenceTail:
      "Keep distance/location renewal-transfer handling inside preview rehearsal only.",
    defaultEvidence:
      "Scenario stays inside the approved preview lane and keeps distance/location renewal-transfer wording bounded, explicit, and rehearsal-only.",
  },
  appointment_rescue_sooner_slot: {
    readySummary:
      "Latest recorded appointment rescue / sooner-slot rehearsal is clear for internal operator review.",
    reviewSummary:
      "Latest recorded appointment rescue / sooner-slot rehearsal needs another tuning pass.",
    blockedSummary:
      "Latest recorded appointment rescue / sooner-slot rehearsal was blocked in the preview lane.",
    defaultSummary:
      "Appointment rescue / sooner-slot rehearsal is ready for internal preview review.",
    readyEvidence:
      "Sooner-slot checks, revised date and time confirmation, and confirmation-email or forms follow-up stay staff-safe without implying a live booking change.",
    reviewEvidenceTail:
      "Keep sooner-slot checks and changed-appointment follow-through explicit without implying live scheduling authority.",
    blockedEvidenceTail:
      "Keep appointment rescue / sooner-slot handling inside preview rehearsal only.",
    defaultEvidence:
      "Scenario stays inside the approved preview lane and keeps appointment rescue / sooner-slot wording bounded, explicit, and rehearsal-only.",
  },
  callback_rescue: {
    readySummary:
      "Latest recorded missed doctor-call recovery rehearsal is clear for internal operator review.",
    reviewSummary:
      "Latest recorded missed doctor-call recovery rehearsal needs another tuning pass.",
    blockedSummary:
      "Latest recorded missed doctor-call recovery rehearsal was blocked in the preview lane.",
    defaultSummary:
      "Missed doctor-call recovery rehearsal is ready for internal preview review.",
    readyEvidence:
      "The missed doctor-call recovery path stays explicit without blame, unsupported callback certainty, or implied live transfer authority.",
    reviewEvidenceTail:
      "Keep the missed doctor-call recovery path explicit and bounded without promising callback certainty or implying live transfer authority.",
    blockedEvidenceTail:
      "Keep missed doctor-call recovery handling inside preview rehearsal only.",
    defaultEvidence:
      "Scenario stays inside the approved preview lane and keeps missed doctor-call recovery wording operator-safe, explicit, and rehearsal-only.",
  },
  transfer_payment_fallback: {
    readySummary:
      "Latest recorded transfer/payment fallback rehearsal is clear for internal operator review.",
    reviewSummary:
      "Latest recorded transfer/payment fallback rehearsal needs another tuning pass.",
    blockedSummary:
      "Latest recorded transfer/payment fallback rehearsal was blocked in the preview lane.",
    defaultSummary:
      "Transfer/payment fallback rehearsal is ready for internal preview review.",
    readyEvidence:
      "Ownership stays explicit and the wording avoids implying a completed live transfer or payment handoff.",
    reviewEvidenceTail:
      "Keep ownership explicit and avoid implying a completed live transfer or payment handoff.",
    blockedEvidenceTail:
      "Keep transfer/payment fallback handling inside preview rehearsal only.",
    defaultEvidence:
      "Scenario stays inside the approved preview lane and keeps transfer/payment fallback wording bounded, explicit, and rehearsal-only.",
  },
};

function getOperationalScenarioExportCopy(
  presetId: StudioScenarioPreset["id"]
) {
  return STUDIO_OPERATIONAL_SCENARIO_EXPORT_COPY[presetId] || null;
}

function scenarioSummaryStatus(params: {
  preset: StudioScenarioPreset;
  allowedChannels: string[];
  improveLoopModel: StudioImproveLoopReadModel;
}) {
  const operationalCopy = getOperationalScenarioExportCopy(params.preset.id);
  const latestRecordedScenario = params.improveLoopModel.evidenceEntries.find(
    (entry) =>
      entry.type === "scenario_run" && entry.scenarioId === params.preset.id
  );

  if (latestRecordedScenario) {
    if (
      latestRecordedScenario.status === "blocked" ||
      latestRecordedScenario.status === "provider_error"
    ) {
      return {
        status: "blocked" as const,
        summary:
          operationalCopy?.blockedSummary ||
          "Latest recorded scenario run was blocked in the preview lane.",
        evidence: `${latestRecordedScenario.sourceLabel} recorded ${latestRecordedScenario.recordedAt}${latestRecordedScenario.requestId ? ` with request ${latestRecordedScenario.requestId}` : ""}.${operationalCopy ? ` ${operationalCopy.blockedEvidenceTail}` : ""}`,
        latestEvidence: {
          recordedAt: latestRecordedScenario.recordedAt,
          requestId: latestRecordedScenario.requestId,
          personaLabel: latestRecordedScenario.personaLabel,
          sourceLabel: latestRecordedScenario.sourceLabel,
        },
      };
    }

    if (
      latestRecordedScenario.status === "review" ||
      latestRecordedScenario.humanization?.total.status === "warn"
    ) {
      return {
        status: "review" as const,
        summary:
          operationalCopy?.reviewSummary ||
          "Latest recorded scenario run needs another tuning pass.",
        evidence: `${latestRecordedScenario.sourceLabel} used ${latestRecordedScenario.personaLabel || "the preview persona"} and scored ${latestRecordedScenario.humanization?.total.score || 0}/${latestRecordedScenario.humanization?.total.maxScore || 60} (${latestRecordedScenario.humanization?.total.status || "review"}).${operationalCopy ? ` ${operationalCopy.reviewEvidenceTail}` : ""}`,
        latestEvidence: {
          recordedAt: latestRecordedScenario.recordedAt,
          requestId: latestRecordedScenario.requestId,
          personaLabel: latestRecordedScenario.personaLabel,
          sourceLabel: latestRecordedScenario.sourceLabel,
        },
      };
    }

    return {
      status: "ready" as const,
      summary:
        operationalCopy?.readySummary ||
        "Latest recorded scenario run is ready for internal preview rehearsal.",
      evidence: `${latestRecordedScenario.sourceLabel} used ${latestRecordedScenario.personaLabel || "the preview persona"}${latestRecordedScenario.requestId ? ` with request ${latestRecordedScenario.requestId}` : ""}.${operationalCopy ? ` ${operationalCopy.readyEvidence}` : ""}`,
      latestEvidence: {
        recordedAt: latestRecordedScenario.recordedAt,
        requestId: latestRecordedScenario.requestId,
        personaLabel: latestRecordedScenario.personaLabel,
        sourceLabel: latestRecordedScenario.sourceLabel,
      },
    };
  }

  if (!params.allowedChannels.includes(params.preset.channel)) {
    return {
      status: "blocked" as const,
      summary: "Blocked from the current preview lane.",
      evidence: `Channel ${params.preset.channel} is not in the current allowed scenario-channel set.`,
      latestEvidence: null,
    };
  }

  if (params.preset.id === "objection_handling") {
    const objectionInteraction = params.improveLoopModel.recentInteractions.find(
      (interaction) => interaction.stage === "objection"
    );

    if (objectionInteraction?.qaStatus !== "pass") {
      return {
        status: "review" as const,
        summary: "Needs another tuning pass before premium demo handoff.",
        evidence: `${objectionInteraction?.qaLabel || "Current objection sample remains under review"}; friction: ${(objectionInteraction?.flaggedFriction || []).join(", ") || "none listed"}.`,
        latestEvidence: null,
      };
    }
  }

  return {
    status: "ready" as const,
    summary:
      operationalCopy?.defaultSummary ||
      "Ready for internal preview and premium demo rehearsal.",
    evidence:
      operationalCopy?.defaultEvidence ||
      `Scenario stays inside ${params.preset.channel} and remains preview-only.`,
    latestEvidence: null,
  };
}

function buildScenarioResultsSummary(params: {
  allowedChannels: string[];
  improveLoopModel: StudioImproveLoopReadModel;
}) {
  const scenarios = STUDIO_SCENARIO_PRESETS.map((preset) => {
    const state = scenarioSummaryStatus({
      preset,
      allowedChannels: params.allowedChannels,
      improveLoopModel: params.improveLoopModel,
    });

    return {
      id: preset.id,
      label: preset.label,
      channel: preset.channel,
      status: state.status,
      summary: state.summary,
      evidence: state.evidence,
      latestEvidence: state.latestEvidence,
    };
  });

  return {
    totalScenarios: scenarios.length,
    readyCount: scenarios.filter((scenario) => scenario.status === "ready").length,
    reviewCount: scenarios.filter((scenario) => scenario.status === "review").length,
    blockedCount: scenarios.filter((scenario) => scenario.status === "blocked").length,
    scenarios,
  };
}

function humanizationDimensionExtremes(
  improveLoopModel: StudioImproveLoopReadModel
) {
  const dimensions = buildStudioHumanizationDimensionDisplays(
    improveLoopModel.humanization.currentPreview
  );
  const strongest = [...dimensions].sort((left, right) => {
    if (right.score !== left.score) {
      return right.score - left.score;
    }

    return left.label.localeCompare(right.label);
  })[0];
  const weakest = [...dimensions].sort((left, right) => {
    if (left.score !== right.score) {
      return left.score - right.score;
    }

    return left.label.localeCompare(right.label);
  })[0];

  return {
    strongestDimension: strongest?.label || "Not scored",
    weakestDimension: weakest?.label || "Not scored",
  };
}

function buildExportNextStepChecklist(params: {
  workspaceState: StudioWorkspaceState;
  premiumDemo: StudioPremiumDemoModel;
}): StudioChecklistItem[] {
  return [
    {
      label: "Approved brief and persona reviewed",
      status: params.workspaceState.deployment.readinessSelections.personaReviewed
        ? "done"
        : "pending",
      detail:
        "Confirm the approved brief and persona still match the intended demo or operator handoff.",
    },
    {
      label: "Launch-control blockers reviewed",
      status: params.workspaceState.deployment.readinessSelections.routingTruthConfirmed
        ? "done"
        : "pending",
      detail:
        "Review launch-control blockers before treating the package as deployment-ready.",
    },
    {
      label: "Preview evidence verified",
      status: params.workspaceState.deployment.readinessSelections.previewLaneConfirmed
        ? "done"
        : "pending",
      detail:
        "Validate preview evidence and scenario summaries against the current internal-preview lane.",
    },
    {
      label: "Operator handoff only",
      status: "pending",
      detail:
        "Use this package for operator review, founder demo prep, rollout review, or handoff only.",
    },
    {
      label: "No live rollout from export",
      status: "blocked",
      detail:
        "Do not treat export generation as production activation, public tenant promotion, or live clinic rollout.",
    },
  ];
}

function buildPublishNextStepChecklist(params: {
  workspaceState: StudioWorkspaceState;
}): StudioChecklistItem[] {
  const checklist = getStudioPublishApprovalChecklistItems(params.workspaceState);
  const currentBlockersReviewed = checklist.find(
    (item) => item.id === "currentBlockersReviewed"
  )?.acknowledged;
  const canaryApprovalConfirmed = checklist.find(
    (item) => item.id === "canaryApprovalExternalConfirmed"
  )?.acknowledged;

  return [
    {
      label: "Internal approval confirmed",
      status: params.workspaceState.deployment.approvalRecordedAt ? "done" : "pending",
      detail:
        "Confirm internal approval reflects the current brief, persona, and draft controls.",
    },
    {
      label: "Blocker state reviewed",
      status: currentBlockersReviewed ? "done" : "pending",
      detail:
        "Review blocker state before promoting this package beyond internal operator use.",
    },
    {
      label: "Preview evidence and readiness current",
      status: params.workspaceState.deployment.readinessSelections.previewLaneConfirmed
        ? "done"
        : "pending",
      detail:
        "Verify preview evidence, readiness notes, and launch-control posture are still current.",
    },
    {
      label: "Internal handoff artifact only",
      status: canaryApprovalConfirmed ? "done" : "pending",
      detail:
        "Treat this handoff package as an internal handoff artifact and rollout-review surface, not a deployment trigger.",
    },
    {
      label: "Separate deployment workflow required",
      status: "blocked",
      detail:
        "Escalate live rollout and public promotion decisions through the separate deployment workflow only.",
    },
  ];
}

export function buildStudioExportPackage(params: {
  generatedAt?: string;
  workspaceState: StudioWorkspaceState;
  premiumDemo: StudioPremiumDemoModel;
  improveLoopModel: StudioImproveLoopReadModel;
  deploymentDecision?: StudioDeploymentDecision | null;
}) {
  const persona = getStudioWorkspaceVoicePersona(
    params.workspaceState.voicePersona.selectedPersonaId
  );
  const selectedVoiceOption = getStudioApprovedVoiceOption(
    params.workspaceState.voicePersona.selectedVoiceOptionId
  );
  const currentHumanization = params.improveLoopModel.humanization.currentPreview;
  const dimensions = buildStudioHumanizationDimensionDisplays(currentHumanization).map(
    (dimension) => ({
      key: dimension.key,
      label: dimension.label,
      score: dimension.score,
      maxScore: dimension.maxScore,
      status: dimension.status,
    })
  );
  const scenarioResultsSummary = buildScenarioResultsSummary({
    allowedChannels: params.premiumDemo.voiceSummary.channelLabels,
    improveLoopModel: params.improveLoopModel,
  });
  const improvedDimensions = params.improveLoopModel.humanization.comparison.dimensions
    .filter((dimension) => dimension.delta > 0)
    .map((dimension) => dimension.label);
  const exportDecision =
    params.deploymentDecision || buildFallbackDeploymentDecision(params.premiumDemo);
  const hasDeploymentDecision = Boolean(params.deploymentDecision);
  const canarySummary = buildStudioCanaryStateSummary({
    deploymentState: exportDecision.state,
    launchControlReady: hasDeploymentDecision
      ? deploymentLaunchControlReady(exportDecision)
      : false,
    rolloutManifestPresent: hasDeploymentDecision
      ? deploymentRolloutManifestPresent(exportDecision)
      : false,
    rolloutEnabled: false,
    canaryEligible: false,
    internalApprovalRecorded: Boolean(params.workspaceState.deployment.approvalRecordedAt),
    approvalStatus: params.workspaceState.deployment.approvalRecordedAt
      ? "approved_prep_only"
      : "pending_owner_approval",
    tenantCanaryAllowed: false,
    twilioRoutingUntouched: true,
    blockedReasons: exportDecision.blockers.map((blocker) => blocker.code),
    tenantScopeLabel: "Current Studio tenant rollout",
  });

  const exportPackage: StudioExportPackage = {
    version: STUDIO_EXPORT_PACKAGE_VERSION,
    mode: "audit_safe_demo_package",
    generatedAt: params.generatedAt || new Date().toISOString(),
    auditSafe: true,
    deploymentActivationTriggered: false,
    nonLiveBoundaryStatement:
      "This export package is for internal review, founder demo prep, rollout review, and operator handoff only. Generating it does not activate live clinic routing, production voice, or tenant rollout.",
    supervisedBookingMilestone: {
      runtimeReadyLabel: "Runtime-ready for supervised booking test only",
      milestoneProofStatus: "pending_first_supervised_real_booking",
      milestoneProofLabel: "Milestone proof pending",
      milestoneProofDetail:
        "Runtime-ready checks, founder-demo packaging, preview evidence, and handoff approval do not complete the supervised-booking milestone. Milestone proof remains pending until one supervised real booking against the live Acuity pool is run and saved with the required evidence bundle.",
      firstBundleRequirements: [
        "Exact offer selected",
        "Booked slot in clinic-local time",
        "Acuity appointment reference",
        "Supervising operator and date",
        "Recorded-at timestamp",
        "Updated owner packet location",
      ],
    },
    assistantBrief: {
      assistantName: params.premiumDemo.brief.assistantName,
      description: params.premiumDemo.brief.description,
      starterTone: params.premiumDemo.brief.starterTone,
      starterPrompt: params.premiumDemo.brief.starterPrompt,
      operatorDescription: params.workspaceState.assistantBrief.operatorDescription,
      recommendedCapabilities: params.premiumDemo.brief.recommendedCapabilities.map(
        (capability) => capability.label
      ),
      blockedCapabilities: params.premiumDemo.brief.blockedCapabilities
        .filter((capability) => capability.detectedFromInput)
        .map((capability) => capability.label),
      humanizationToggles: getEnabledHumanizationLabels(
        params.workspaceState.humanization
      ),
    },
    previewPersonaSelection: {
      personaId: persona.id,
      personaLabel: persona.label,
      personaSummary: persona.summary,
      approvedVoiceId: selectedVoiceOption.id,
      approvedVoiceLabel: selectedVoiceOption.label,
      approvedVoiceScope: selectedVoiceOption.selectionScopeLabel,
      selectedWorkspacePreviewMode: params.workspaceState.preview.selectedChannel,
      providerMode: params.premiumDemo.voiceSummary.providerMode,
      modelLabel: params.premiumDemo.voiceSummary.modelLabel,
      allowedScenarioChannels: [...params.premiumDemo.voiceSummary.channelLabels],
      routingNote: params.premiumDemo.voiceSummary.routingNote,
    },
    scenarioResultsSummary,
    humanizationScoreSummary: {
      requestId: currentHumanization.requestId,
      status: currentHumanization.humanization.total.status,
      totalScore: currentHumanization.humanization.total.score,
      maxScore: currentHumanization.humanization.total.maxScore,
      dimensions,
      recommendations: params.improveLoopModel.humanization.tuningRecommendations.map(
        (recommendation) => recommendation.title
      ),
      comparison: {
        overallDelta: params.improveLoopModel.humanization.comparison.overallDelta,
        headline: params.improveLoopModel.humanization.comparison.headline,
        improvedDimensions,
      },
    },
    deploymentReadinessStatus: {
      state: exportDecision.state,
      stateLabel:
        exportDecision.state === "deploy_blocked"
          ? params.premiumDemo.deployReadiness.gate.actionLabel
          : exportDecision.headline,
      summary: exportDecision.summary,
      blockers: exportDecision.blockers.map((blocker) => blocker.detail),
      canaryRestrictions: [
        canarySummary.promotionNote,
        "Current rollout manifest controls remain authoritative.",
        "No live clinic activation from export.",
      ],
    },
    rolloutFlags: {
      previewOnlyUntilA21: true,
      twilioProductionRoutingUntouched: true,
      nonLiveBoundaryStatement:
        "Export generation remains preview-safe and does not trigger tenant rollout or production routing.",
      liveDeploymentEnabled: false,
      webhookCutoverEnabled: false,
      activationPolicy: "manifest_only_no_live_activation",
    },
    canarySummary: {
      state: canarySummary.state,
      stateLabel: canarySummary.stateLabel,
      headline: canarySummary.headline,
      summary: canarySummary.summary,
      tenantRestrictions: [...canarySummary.tenantRestrictions],
      promotionBlocked: canarySummary.promotionBlocked,
      promotionNote: canarySummary.promotionNote,
    },
    nextStepChecklist: buildExportNextStepChecklist({
      workspaceState: params.workspaceState,
      premiumDemo: params.premiumDemo,
    }),
    operatorNotes: params.workspaceState.deployment.notes.trim()
      ? params.workspaceState.deployment.notes
      : null,
  };

  return sanitizeStudioExportValue(exportPackage);
}

export function buildStudioPublishPackage(params: {
  generatedAt?: string;
  workspaceState: StudioWorkspaceState;
  premiumDemo: StudioPremiumDemoModel;
  improveLoopModel: StudioImproveLoopReadModel;
  deploymentDecision?: StudioDeploymentDecision | null;
}) {
  const persona = getStudioWorkspaceVoicePersona(
    params.workspaceState.voicePersona.selectedPersonaId
  );
  const selectedVoiceOption = getStudioApprovedVoiceOption(
    params.workspaceState.voicePersona.selectedVoiceOptionId
  );
  const generatedAt = params.generatedAt || new Date().toISOString();
  const decision =
    params.deploymentDecision || buildFallbackDeploymentDecision(params.premiumDemo);
  const hasDeploymentDecision = Boolean(params.deploymentDecision);
  const checklist = getStudioPublishApprovalChecklistItems(params.workspaceState);
  const checklistComplete = checklist.every((item) => item.acknowledged);
  const approvalTimestamp = params.workspaceState.deployment.approvalRecordedAt;
  const approvalStatus = approvalTimestamp
    ? "signed_off_internal_only"
    : checklistComplete
      ? "ready_for_internal_signoff"
      : "pending_acknowledgements";
  const scenarioRunEntries = params.improveLoopModel.evidenceEntries.filter(
    (entry) => entry.type === "scenario_run"
  );
  const textPreviewEntries = params.improveLoopModel.evidenceEntries.filter(
    (entry) => entry.type === "text_preview"
  );
  const latestEvidence = params.improveLoopModel.evidenceEntries[0] || null;
  const currentHumanization = params.improveLoopModel.humanization.currentPreview;
  const { strongestDimension, weakestDimension } = humanizationDimensionExtremes(
    params.improveLoopModel
  );
  const canarySummary = buildStudioCanaryStateSummary({
    deploymentState: decision.state,
    launchControlReady: hasDeploymentDecision
      ? deploymentLaunchControlReady(decision)
      : false,
    rolloutManifestPresent: hasDeploymentDecision
      ? deploymentRolloutManifestPresent(decision)
      : false,
    rolloutEnabled:
      decision.state === "canary_blocked" || decision.state === "canary_ready",
    canaryEligible: decision.state === "canary_ready",
    internalApprovalRecorded: Boolean(approvalTimestamp),
    approvalStatus: approvalTimestamp
      ? decision.state === "canary_ready"
        ? "approved_for_a21_canary"
        : "approved_prep_only"
      : "pending_owner_approval",
    tenantCanaryAllowed: false,
    twilioRoutingUntouched: true,
    blockedReasons: decision.blockers.map((blocker) => blocker.code),
    tenantScopeLabel: "Current Studio tenant rollout",
  });

  const publishPackage: StudioPublishPackage = {
    version: STUDIO_PUBLISH_PACKAGE_VERSION,
    mode: "internal_only_publish_package",
    generatedAt,
    internalOnly: true,
    deploymentActivationTriggered: false,
    liveDeploymentEnabled: false,
    twilioProductionRoutingUntouched: true,
    nonLiveBoundaryStatement:
      "This handoff package records internal approval and prepares operator handoff and rollout review. It does not activate production deployment, change live clinic routing, or bypass launch-control blockers.",
    supervisedBookingMilestone: {
      runtimeReadyLabel: "Runtime-ready for supervised booking test only",
      milestoneProofStatus: "pending_first_supervised_real_booking",
      milestoneProofLabel: "Milestone proof pending",
      milestoneProofDetail:
        "Internal signoff, readiness review, and preview evidence do not complete the supervised-booking milestone. Milestone proof remains pending until one supervised real booking against the live Acuity pool is run and saved with the required evidence bundle.",
      firstBundleRequirements: [
        "Exact offer selected",
        "Booked slot in clinic-local time",
        "Acuity appointment reference",
        "Supervising operator and date",
        "Recorded-at timestamp",
        "Updated owner packet location",
      ],
    },
    approval: {
      status: approvalStatus,
      statusLabel:
        approvalStatus === "signed_off_internal_only"
          ? "Signed off for internal discussion only"
          : approvalStatus === "ready_for_internal_signoff"
            ? "Ready for internal sign-off"
            : "Awaiting operator acknowledgement",
      approvalTimestamp,
      checklistComplete,
      checklist: checklist.map((item) => ({
        id: item.id,
        label: item.label,
        detail: item.detail,
        acknowledged: item.acknowledged,
      })),
    },
    approvedBriefSnapshot: {
      assistantName: params.premiumDemo.brief.assistantName,
      description: params.premiumDemo.brief.description,
      starterTone: params.premiumDemo.brief.starterTone,
      starterPrompt: params.premiumDemo.brief.starterPrompt,
      operatorDescription: params.workspaceState.assistantBrief.operatorDescription,
      recommendedCapabilities: params.premiumDemo.brief.recommendedCapabilities.map(
        (capability) => capability.label
      ),
      blockedCapabilities: params.premiumDemo.brief.blockedCapabilities
        .filter((capability) => capability.detectedFromInput)
        .map((capability) => capability.label),
    },
    approvedPersonaAndControls: {
      personaId: persona.id,
      personaLabel: persona.label,
      personaSummary: persona.summary,
      approvedVoiceId: selectedVoiceOption.id,
      approvedVoiceLabel: selectedVoiceOption.label,
      approvedVoiceScope: selectedVoiceOption.selectionScopeLabel,
      selectedWorkspacePreviewMode: params.workspaceState.preview.selectedChannel,
      providerMode: params.premiumDemo.voiceSummary.providerMode,
      modelLabel: params.premiumDemo.voiceSummary.modelLabel,
      enabledHumanizationToggles: getEnabledHumanizationLabels(
        params.workspaceState.humanization
      ),
      deploymentNotes: params.workspaceState.deployment.notes.trim()
        ? params.workspaceState.deployment.notes
        : null,
      readinessAcknowledgements: getStudioPublishApprovalChecklistItems(
        params.workspaceState
      )
        .filter((item) => item.source === "readiness" && item.acknowledged)
        .map((item) => item.label),
      approvalAcknowledgements: checklist
        .filter((item) => item.source === "approval" && item.acknowledged)
        .map((item) => item.label),
    },
    previewEvidenceSummary: {
      totalEntries: params.improveLoopModel.evidenceEntries.length,
      scenarioRunCount: scenarioRunEntries.length,
      textPreviewCount: textPreviewEntries.length,
      latestRecordedAt: latestEvidence?.recordedAt || null,
      latestRequestId: latestEvidence?.requestId || null,
      latestScenarioLabel:
        latestEvidence?.scenarioLabel || latestEvidence?.sourceLabel || null,
      latestChannel: latestEvidence?.channel || null,
      latestPersonaLabel: latestEvidence?.personaLabel || null,
    },
    humanizationSummary: {
      requestId: currentHumanization.requestId,
      status: currentHumanization.humanization.total.status,
      totalScore: currentHumanization.humanization.total.score,
      maxScore: currentHumanization.humanization.total.maxScore,
      strongestDimension,
      weakestDimension,
      comparisonHeadline: params.improveLoopModel.humanization.comparison.headline,
    },
    readinessDecision: {
      state: decision.state,
      stateLabel: decision.stateLabel,
      headline: decision.headline,
      summary: decision.summary,
      blockerCount: decision.blockers.length,
      nextActions: [...decision.nextActions],
    },
    blockerList: decision.blockers.map(
      (blocker) => `${blocker.label}: ${blocker.detail}`
    ),
    canaryRestrictions: [
      "Preview-only posture remains in force.",
      "Current rollout manifest controls remain authoritative.",
      "External rollout approval stays outside Studio.",
      "No live deployment, webhook cutover, or Twilio routing change is triggered here.",
    ],
    canarySummary: {
      state: canarySummary.state,
      stateLabel: canarySummary.stateLabel,
      headline: canarySummary.headline,
      summary: canarySummary.summary,
      tenantRestrictions: [...canarySummary.tenantRestrictions],
      promotionBlocked: canarySummary.promotionBlocked,
      promotionNote: canarySummary.promotionNote,
    },
    nextStepChecklist: buildPublishNextStepChecklist({
      workspaceState: params.workspaceState,
    }),
  };

  return sanitizeStudioExportValue(publishPackage);
}

export function formatStudioExportMarkdownSummary(
  exportPackage: StudioExportPackage
) {
  return [
    "# VetCan Studio Export Package",
    "",
    `Generated: ${exportPackage.generatedAt}`,
    `Version: ${exportPackage.version}`,
    `Mode: ${exportPackage.mode}`,
    "",
    "## Boundary Statement",
    exportPackage.nonLiveBoundaryStatement,
    "",
    "## Supervised Booking Milestone",
    `- Runtime posture: ${exportPackage.supervisedBookingMilestone.runtimeReadyLabel}`,
    `- Milestone status: ${exportPackage.supervisedBookingMilestone.milestoneProofLabel}`,
    `- Detail: ${exportPackage.supervisedBookingMilestone.milestoneProofDetail}`,
    ...exportPackage.supervisedBookingMilestone.firstBundleRequirements.map(
      (requirement) => `- First bundle requirement: ${requirement}`
    ),
    "",
    "## Assistant Brief",
    `- Assistant: ${exportPackage.assistantBrief.assistantName}`,
    `- Tone: ${exportPackage.assistantBrief.starterTone}`,
    `- Recommended capabilities: ${exportPackage.assistantBrief.recommendedCapabilities.join(", ") || "None listed"}`,
    `- Blocked capability detections: ${exportPackage.assistantBrief.blockedCapabilities.join(", ") || "None detected"}`,
    "",
    "## Preview Persona Selection",
    `- Persona: ${exportPackage.previewPersonaSelection.personaLabel}`,
    `- Approved AI voice: ${exportPackage.previewPersonaSelection.approvedVoiceLabel} (${exportPackage.previewPersonaSelection.approvedVoiceScope})`,
    `- Workspace preview mode: ${exportPackage.previewPersonaSelection.selectedWorkspacePreviewMode}`,
    `- Allowed scenario channels: ${exportPackage.previewPersonaSelection.allowedScenarioChannels.join(", ")}`,
    `- Routing note: ${exportPackage.previewPersonaSelection.routingNote}`,
    "",
    "## Scenario Results Summary",
    ...exportPackage.scenarioResultsSummary.scenarios.map(
      (scenario) =>
        `- ${scenario.label}: ${scenario.status} — ${scenario.evidence}${scenario.latestEvidence?.requestId ? ` (request ${scenario.latestEvidence.requestId})` : ""}`
    ),
    "",
    "## Humanization Score Summary",
    `- Current score: ${exportPackage.humanizationScoreSummary.totalScore}/${exportPackage.humanizationScoreSummary.maxScore} (${exportPackage.humanizationScoreSummary.status})`,
    `- Request ID: ${exportPackage.humanizationScoreSummary.requestId}`,
    `- Comparison headline: ${exportPackage.humanizationScoreSummary.comparison.headline}`,
    `- Improved dimensions: ${exportPackage.humanizationScoreSummary.comparison.improvedDimensions.join(", ") || "None"}`,
    "",
    "## Deployment And Canary Posture",
    `- State: ${exportPackage.deploymentReadinessStatus.stateLabel}`,
    ...exportPackage.deploymentReadinessStatus.blockers.map(
      (blocker) => `- Blocker: ${blocker}`
    ),
    ...exportPackage.deploymentReadinessStatus.canaryRestrictions.map(
      (restriction) => `- Canary restriction: ${restriction}`
    ),
    "",
    "## Controlled Canary Posture",
    `- State: ${exportPackage.canarySummary.stateLabel}`,
    `- Headline: ${exportPackage.canarySummary.headline}`,
    `- Summary: ${exportPackage.canarySummary.summary}`,
    `- Promotion blocked: ${exportPackage.canarySummary.promotionBlocked ? "yes" : "no"}`,
    `- Promotion note: ${exportPackage.canarySummary.promotionNote}`,
    ...exportPackage.canarySummary.tenantRestrictions.map(
      (restriction) => `- Tenant restriction: ${restriction}`
    ),
    "",
    "## Next Steps",
    ...exportPackage.nextStepChecklist.map(
      (item) => `- [${item.status === "done" ? "x" : " "}] ${item.label}: ${item.detail}`
    ),
    exportPackage.operatorNotes ? "" : null,
    exportPackage.operatorNotes ? "## Operator Notes" : null,
    exportPackage.operatorNotes || null,
  ]
    .filter((line): line is string => Boolean(line))
    .join("\n");
}

export function formatStudioPublishMarkdownSummary(
  publishPackage: StudioPublishPackage
) {
  return [
    "# VetCan Studio Approval Handoff Package",
    "",
    `Generated: ${publishPackage.generatedAt}`,
    `Version: ${publishPackage.version}`,
    `Mode: ${publishPackage.mode}`,
    `Approval status: ${publishPackage.approval.statusLabel}`,
    `Approval timestamp: ${publishPackage.approval.approvalTimestamp || "Not yet recorded"}`,
    "",
    "## Boundary Statement",
    publishPackage.nonLiveBoundaryStatement,
    "",
    "## Supervised Booking Milestone",
    `- Runtime posture: ${publishPackage.supervisedBookingMilestone.runtimeReadyLabel}`,
    `- Milestone status: ${publishPackage.supervisedBookingMilestone.milestoneProofLabel}`,
    `- Detail: ${publishPackage.supervisedBookingMilestone.milestoneProofDetail}`,
    ...publishPackage.supervisedBookingMilestone.firstBundleRequirements.map(
      (requirement) => `- First bundle requirement: ${requirement}`
    ),
    "",
    "## Approval Checklist",
    ...publishPackage.approval.checklist.map(
      (item) => `- [${item.acknowledged ? "x" : " "}] ${item.label}: ${item.detail}`
    ),
    "",
    "## Approved Brief Snapshot",
    `- Assistant: ${publishPackage.approvedBriefSnapshot.assistantName}`,
    `- Tone: ${publishPackage.approvedBriefSnapshot.starterTone}`,
    `- Recommended capabilities: ${publishPackage.approvedBriefSnapshot.recommendedCapabilities.join(", ") || "None listed"}`,
    `- Blocked capabilities: ${publishPackage.approvedBriefSnapshot.blockedCapabilities.join(", ") || "None detected"}`,
    "",
    "## Approved Persona And Controls",
    `- Persona: ${publishPackage.approvedPersonaAndControls.personaLabel}`,
    `- Approved AI voice: ${publishPackage.approvedPersonaAndControls.approvedVoiceLabel} (${publishPackage.approvedPersonaAndControls.approvedVoiceScope})`,
    `- Workspace preview mode: ${publishPackage.approvedPersonaAndControls.selectedWorkspacePreviewMode}`,
    `- Humanization toggles: ${publishPackage.approvedPersonaAndControls.enabledHumanizationToggles.join(", ") || "None enabled"}`,
    `- Deployment notes: ${publishPackage.approvedPersonaAndControls.deploymentNotes || "None"}`,
    "",
    "## Preview Evidence Summary",
    `- Total entries: ${publishPackage.previewEvidenceSummary.totalEntries}`,
    `- Scenario runs: ${publishPackage.previewEvidenceSummary.scenarioRunCount}`,
    `- Text previews: ${publishPackage.previewEvidenceSummary.textPreviewCount}`,
    `- Latest request: ${publishPackage.previewEvidenceSummary.latestRequestId || "None recorded"}`,
    "",
    "## Humanization Summary",
    `- Score: ${publishPackage.humanizationSummary.totalScore}/${publishPackage.humanizationSummary.maxScore} (${publishPackage.humanizationSummary.status})`,
    `- Strongest dimension: ${publishPackage.humanizationSummary.strongestDimension}`,
    `- Weakest dimension: ${publishPackage.humanizationSummary.weakestDimension}`,
    `- Comparison headline: ${publishPackage.humanizationSummary.comparisonHeadline}`,
    "",
    "## Readiness Decision",
    `- State: ${publishPackage.readinessDecision.stateLabel}`,
    `- Headline: ${publishPackage.readinessDecision.headline}`,
    `- Summary: ${publishPackage.readinessDecision.summary}`,
    ...publishPackage.blockerList.map((blocker) => `- Blocker: ${blocker}`),
    ...publishPackage.canaryRestrictions.map(
      (restriction) => `- Restriction: ${restriction}`
    ),
    "",
    "## Controlled Canary Posture",
    `- State: ${publishPackage.canarySummary.stateLabel}`,
    `- Headline: ${publishPackage.canarySummary.headline}`,
    `- Summary: ${publishPackage.canarySummary.summary}`,
    `- Promotion blocked: ${publishPackage.canarySummary.promotionBlocked ? "yes" : "no"}`,
    `- Promotion note: ${publishPackage.canarySummary.promotionNote}`,
    ...publishPackage.canarySummary.tenantRestrictions.map(
      (restriction) => `- Tenant restriction: ${restriction}`
    ),
    "",
    "## Next Steps",
    ...publishPackage.nextStepChecklist.map(
      (item) => `- [${item.status === "done" ? "x" : " "}] ${item.label}: ${item.detail}`
    ),
  ].join("\n");
}
